import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('fpa_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('fpa_token');
      localStorage.removeItem('fpa_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Files
export const filesApi = {
  upload: (file, businessUnitId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (businessUnitId) formData.append('business_unit_id', businessUnitId);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: (params = {}) => api.get('/files', { params }),
  getOne: (id) => api.get(`/files/${id}`),
  getData: (id) => api.get(`/files/${id}/data`),
  delete: (id) => api.delete(`/files/${id}`),
};

// Dashboard
export const dashboardApi = {
  getSummary: (params = {}) => api.get('/dashboard/summary', { params }),
  getCashflow: (params = {}) => api.get('/dashboard/cashflow', { params }),
  getVariance: (params = {}) => api.get('/dashboard/variance', { params }),
};

// Insights
export const insightsApi = {
  generate: (fileId, businessUnitId) => api.post('/insights/generate', null, { 
    params: { file_id: fileId, business_unit_id: businessUnitId } 
  }),
  getAll: (params = {}) => api.get('/insights', { params }),
  delete: (id) => api.delete(`/insights/${id}`),
};

// Settings
export const settingsApi = {
  // Global Settings
  getGlobalSettings: () => api.get('/settings/global'),
  updateGlobalSettings: (data) => api.put('/settings/global', data),
  
  // Branding
  getBranding: () => api.get('/settings/branding'),
  updateBranding: (data) => api.put('/settings/branding', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/branding/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadSigninBackground: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/branding/signin-background', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteSigninBackground: () => api.delete('/settings/branding/signin-background'),
  
  // Business Units
  getBusinessUnits: () => api.get('/settings/business-units'),
  createBusinessUnit: (data) => api.post('/settings/business-units', data),
  updateBusinessUnit: (id, data) => api.put(`/settings/business-units/${id}`, data),
  deleteBusinessUnit: (id) => api.delete(`/settings/business-units/${id}`),
  uploadBuLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/settings/business-units/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Accounts
  getAccounts: (category) => api.get('/settings/accounts', { params: { category } }),
  getAccountsHierarchy: () => api.get('/settings/accounts/hierarchy'),
  getAccountCategories: () => api.get('/settings/accounts/categories'),
  createAccount: (data) => api.post('/settings/accounts', data),
  updateAccount: (id, data) => api.put(`/settings/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/settings/accounts/${id}`),
  
  // Cost Centers
  getCostCenters: () => api.get('/settings/cost-centers'),
  createCostCenter: (data) => api.post('/settings/cost-centers', data),
  updateCostCenter: (id, data) => api.put(`/settings/cost-centers/${id}`, data),
  deleteCostCenter: (id) => api.delete(`/settings/cost-centers/${id}`),
  
  // Users
  getUsers: () => api.get('/settings/users'),
  createUser: (data) => api.post('/settings/users', data),
  updateUser: (id, data) => api.put(`/settings/users/${id}`, data),
  deleteUser: (id) => api.delete(`/settings/users/${id}`),
  resendInvite: (id) => api.post(`/settings/users/${id}/resend-invite`),
  resetUserPassword: (id) => api.post(`/settings/users/${id}/reset-password`),
  
  // Job Titles
  getJobTitles: () => api.get('/settings/job-titles'),
  
  // SSO Configuration
  getSSOConfig: () => api.get('/settings/sso'),
  updateSSOConfig: (data) => api.put('/settings/sso', data),
  testSSOConfig: () => api.post('/settings/sso/test'),
};

// SSO Authentication
export const ssoApi = {
  getStatus: () => api.get('/auth/sso/status'),
  getLoginUrl: () => api.get('/auth/sso/login'),
  callback: (code, state) => api.post('/auth/sso/callback', null, { params: { code, state } }),
};

// Budgets
export const budgetsApi = {
  getAll: (params) => api.get('/budgets', { params }),
  getFiscalYears: () => api.get('/budgets/fiscal-years'),
  getOne: (id) => api.get(`/budgets/${id}`),
  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.put(`/budgets/${id}`, data),
  updateLineItems: (id, lineItems) => api.put(`/budgets/${id}/line-items`, lineItems),
  revise: (id, note) => api.put(`/budgets/${id}/revise?revision_note=${encodeURIComponent(note)}`),
  updateStatus: (id, status, comment) => api.put(`/budgets/${id}/status?new_status=${status}${comment ? `&comment=${encodeURIComponent(comment)}` : ''}`),
  delete: (id) => api.delete(`/budgets/${id}`),
  importFromFile: (data) => api.post('/budgets/create', data), // Create budget (optionally from forecast)
  getTemplate: () => api.get('/budgets/template'),
  // Actuals Import
  importActuals: (budgetId, formData) => api.post(`/budgets/${budgetId}/import-actuals`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getActualsImportHistory: (budgetId) => api.get(`/budgets/${budgetId}/actuals-import-history`),
  getActualsTemplate: (budgetId) => api.get(`/budgets/${budgetId}/actuals-template`),
  // Comments
  getComments: (budgetId, params = {}) => api.get(`/budgets/${budgetId}/comments`, { params }),
  addComment: (budgetId, data) => api.post(`/budgets/${budgetId}/comments`, data),
  updateComment: (budgetId, commentId, data) => api.put(`/budgets/${budgetId}/comments/${commentId}`, data),
  deleteComment: (budgetId, commentId) => api.delete(`/budgets/${budgetId}/comments/${commentId}`),
  // Consolidated Views
  getConsolidatedBudgets: (params) => api.get('/budgets/consolidated', { params }),
  // Financial Summary
  getFinancialSummary: (params) => api.get('/budgets/financial-summary', { params }),
};

// Financial Review
export const financialReviewApi = {
  getReviewData: (budgetId, month) => api.get(`/financial-review/${budgetId}/${month}`),
  updateReviewItem: (reviewId, itemId, data) => api.put(`/financial-review/${reviewId}/items/${itemId}`, data),
  lockReview: (reviewId) => api.put(`/financial-review/${reviewId}/lock`),
};

// Review Requests (CFO Approval Workflow)
export const reviewRequestsApi = {
  create: (data) => api.post('/review-requests', data),
  getAll: (params) => api.get('/review-requests', { params }),
  getById: (requestId) => api.get(`/review-requests/${requestId}`),
  getPendingCount: () => api.get('/review-requests/pending-count'),
  update: (requestId, data) => api.put(`/review-requests/${requestId}`, data),
};

// Notifications
export const notificationsApi = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
};

// Forecasts
export const forecastsApi = {
  getAll: (params) => api.get('/forecasts', { params }),
  getById: (forecastId) => api.get(`/forecasts/${forecastId}`),
  create: (data) => api.post('/forecasts', data),
  update: (forecastId, data) => api.put(`/forecasts/${forecastId}`, data),
  updateLineItems: (forecastId, lineItems) => api.put(`/forecasts/${forecastId}/line-items`, lineItems),
  updateStatus: (forecastId, status) => api.put(`/forecasts/${forecastId}/status`, null, { params: { new_status: status } }),
  delete: (forecastId) => api.delete(`/forecasts/${forecastId}`),
  getConsolidated: (params) => api.get('/forecasts/consolidated', { params }),
  // Scenario comparison & adjustments
  compare: (forecastIds) => api.get('/forecasts/compare', { params: { forecast_ids: forecastIds.join(',') } }),
  applyAdjustment: (forecastId, adjustment) => api.post(`/forecasts/${forecastId}/apply-adjustment`, adjustment),
  // Rollover to next FY
  rollover: (forecastId, newFiscalYear, newName) => api.post(`/forecasts/${forecastId}/rollover`, { fiscal_year: newFiscalYear, name: newName }),
};

// Cashflows
export const cashflowsApi = {
  getAll: (params) => api.get('/cashflows', { params }),
  getById: (cashflowId) => api.get(`/cashflows/${cashflowId}`),
  create: (data) => api.post('/cashflows', data),
  updateLineItems: (cashflowId, lineItems) => api.put(`/cashflows/${cashflowId}/line-items`, lineItems),
  updateSettings: (cashflowId, settings) => api.put(`/cashflows/${cashflowId}/settings`, settings),
  updateStatus: (cashflowId, status) => api.put(`/cashflows/${cashflowId}/status`, { status }),
  delete: (cashflowId) => api.delete(`/cashflows/${cashflowId}`),
  getSummary: (cashflowId) => api.get(`/cashflows/${cashflowId}/summary`),
  // What-If Scenarios
  compare: (cashflowIds) => api.get('/cashflows/compare', { params: { cashflow_ids: cashflowIds.join(',') } }),
  duplicate: (cashflowId, scenarioType, newName) => api.post(`/cashflows/${cashflowId}/duplicate`, null, { 
    params: { new_scenario_type: scenarioType, new_name: newName } 
  }),
};

// Payment Terms
export const paymentTermsApi = {
  getAll: () => api.get('/payment-terms'),
  create: (data) => api.post('/payment-terms', data),
  delete: (termId) => api.delete(`/payment-terms/${termId}`),
};

export default api;
